import { Component } from '@theme/component';
import { track } from '@theme/r-analytics';

/* ↗ .cursor/references/code-notes/r-pdp-subscribe.js.md#§1 */
class RPdpSubscribe extends Component {
  /** @type {HTMLElement | null} */ #panel = null;
  /** @type {HTMLElement | null} */ #frequency = null;
  /** @type {HTMLInputElement | null} */ #input = null;

  /** Per-label: the last text written by something other than us (Liquid, morph, sold-out). */
  /** @type {WeakMap<HTMLElement, string>} */ #atcDefaults = new WeakMap();
  /** Per-label: the last text we wrote, so the observer can tell our writes from theirs. */
  /** @type {WeakMap<HTMLElement, string>} */ #atcApplied = new WeakMap();
  /** @type {MutationObserver | null} */ #atcObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#panel = this.#findInSection('[data-r-panel]');
    this.#frequency = this.#panel?.querySelector('[data-r-frequency]') ?? null;

    // The toggle radios live inside this element, so their change bubbles here.
    this.addEventListener('change', this.#onChange);
    // Frequency radios live in the (separate) panel — listen on it directly.
    this.#frequency?.addEventListener('change', this.#onChange);

    this.#watchAddToCart();

    // Reflect the initial (server-rendered) state without changing layout.
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#frequency?.removeEventListener('change', this.#onChange);
    this.#atcObserver?.disconnect();
    this.#atcObserver = null;
  }

  /** @param {string} selector @returns {HTMLElement | null} */
  #findInSection(selector) {
    const sectionId = this.dataset.sectionId;
    const scope =
      (sectionId && document.querySelector(`product-form-component[data-section-id="${sectionId}"]`)) ||
      this.closest('.shopify-section') ||
      document;
    const found = scope.querySelector(selector);
    return found instanceof HTMLElement ? found : null;
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const sectionId = this.dataset.sectionId;
    const bySection =
      sectionId &&
      document.querySelector(`product-form-component[data-section-id="${sectionId}"] form`);
    if (bySection instanceof HTMLFormElement) return bySection;
    const scoped = this.closest('.shopify-section')?.querySelector('product-form-component form');
    return scoped instanceof HTMLFormElement ? scoped : null;
  }

  /** Ensure the owned hidden `selling_plan` input exists inside the product form. */
  #ensureInput() {
    const form = this.#form;
    if (!form) return null;
    if (this.#input && this.#input.isConnected && form.contains(this.#input)) return this.#input;

    let input = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="selling_plan"][data-r-pdp-subscribe]')
    );
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.setAttribute('data-r-pdp-subscribe', '');
      form.appendChild(input);
    }
    this.#input = input;
    return input;
  }

  /** @returns {boolean} */
  get #subscribed() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-r-toggle] input[type="radio"]:checked')
    );
    return checked?.value === 'subscribe';
  }

  /** @returns {string | undefined} The PDP product this toggle belongs to. */
  #productId() {
    const sectionId = this.dataset.sectionId;
    const form =
      (sectionId && document.querySelector(`product-form-component[data-section-id="${sectionId}"]`)) ||
      this.closest('.shopify-section')?.querySelector('product-form-component');
    return form instanceof HTMLElement ? form.dataset.productId : undefined;
  }

  /** @returns {string} The selling-plan id for the chosen frequency (or default). */
  #planId() {
    const freq = /** @type {HTMLInputElement | null} */ (
      this.#frequency?.querySelector('input[type="radio"]:checked')
    );
    return freq?.value || this.dataset.defaultPlan || '';
  }

  /* ↗ .cursor/references/code-notes/r-pdp-subscribe.js.md#§2 */

  /** @returns {Element[]} The containers that survive re-render and hold the buttons. */
  #atcRoots() {
    const scope = this.closest('.shopify-section') ?? this.#form;
    if (!scope) return [];
    return [...scope.querySelectorAll('product-form-component, sticky-add-to-cart')];
  }

  /** @returns {HTMLElement[]} Every add-to-cart label element in this section. */
  #atcTextEls() {
    const els = [];
    for (const root of this.#atcRoots()) {
      for (const content of root.querySelectorAll('.add-to-cart-text__content')) {
        // Horizon nests spans inside for its swap animation; write to the innermost.
        const inner = content.querySelector('span span') ?? content.querySelector('span');
        const el = inner ?? content;
        if (el instanceof HTMLElement) els.push(el);
      }
    }
    return els;
  }

  /** @returns {string} The visible label of the checked frequency, if any. */
  #frequencyLabel() {
    const checked = this.#frequency?.querySelector('input[type="radio"]:checked');
    const text = checked?.nextElementSibling;
    return text instanceof HTMLElement ? (text.textContent ?? '').trim() : '';
  }

  /**
   * @param {HTMLElement} el
   * @returns {string | null} The label to show, or null to leave this button alone.
   */
  #atcLabel(el) {
    const fallback = this.#atcDefaults.get(el) ?? null;
    if (!this.#subscribed) return fallback;

    const button = el.closest('button');
    // Sold out / unavailable own the label — never overwrite those.
    if (button instanceof HTMLButtonElement && button.disabled) return null;

    const template = this.dataset.atcSubscribe ?? '';
    const token = this.dataset.atcFreqToken ?? '';
    const plain = this.dataset.atcSubscribePlain ?? '';
    const frequency = this.#frequencyLabel();

    // One selling plan means no frequency group, so fall back to the plain wording
    // rather than rendering the raw token.
    if (!frequency || !template || !token) return plain || fallback;
    return template.replace(token, frequency);
  }

  #syncAddToCart() {
    for (const el of this.#atcTextEls()) {
      const current = (el.textContent ?? '').trim();
      if (!this.#atcDefaults.has(el)) this.#atcDefaults.set(el, current);

      const label = this.#atcLabel(el);
      if (label == null || label === current) continue;

      el.textContent = label;
      this.#atcApplied.set(el, label);
    }
  }

  #watchAddToCart() {
    this.#atcObserver?.disconnect();
    this.#atcObserver = new MutationObserver(() => {
      for (const el of this.#atcTextEls()) {
        const current = (el.textContent ?? '').trim();
        // Our own write — leave the baseline alone.
        if (current === this.#atcApplied.get(el)) continue;
        // Someone else rewrote it (morph, sticky-bar replacement, sold-out swap).
        this.#atcDefaults.set(el, current);
      }
      // Terminates: the re-apply writes values that match #atcApplied, so the
      // observer pass it triggers finds nothing to reconcile and writes nothing.
      this.#syncAddToCart();
    });

    for (const root of this.#atcRoots()) {
      this.#atcObserver.observe(root, { childList: true, characterData: true, subtree: true });
    }
  }

  #sync() {
    const subscribed = this.#subscribed;
    const input = this.#ensureInput();
    if (input) input.value = subscribed ? this.#planId() : '';

    if (this.#panel) {
      this.#panel.dataset.expanded = subscribed ? 'true' : 'false';
      if (subscribed) this.#panel.removeAttribute('inert');
      else this.#panel.setAttribute('inert', '');
    }
    this.dataset.active = subscribed ? 'true' : 'false';
    this.#syncAddToCart();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    this.#sync();

    const isToggle = Boolean(target.closest('[data-r-toggle]'));
    const subscribed = this.#subscribed;
    track('subscription_select', {
      context: 'pdp',
      action: isToggle ? (subscribed ? 'subscribe' : 'one_time') : 'frequency_change',
      selling_plan_id: subscribed ? this.#planId() : undefined,
      frequency: subscribed ? this.#frequencyLabel() : undefined,
      product_id: this.#productId(),
    });
  };
}

if (!customElements.get('r-pdp-subscribe')) {
  customElements.define('r-pdp-subscribe', RPdpSubscribe);
}
